import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from '../api';

/**
 * One box for everything (D-340): orders, tickets by short code, customers,
 * events, organizations, group holds and donors. Ctrl+K (⌘K) from anywhere.
 *
 * The server searches only what this person may open, so the box never shows
 * a result that leads to "not permitted".
 */

interface SearchResult {
  kind: 'order' | 'ticket' | 'customer' | 'event' | 'organization' | 'group_hold' | 'donation';
  id: string;
  title: string;
  detail: string | null;
  href: string;
}

const KIND_LABEL: Record<SearchResult['kind'], string> = {
  order: 'Order',
  ticket: 'Ticket',
  customer: 'Customer',
  event: 'Event',
  organization: 'Organization',
  group_hold: 'Group hold',
  donation: 'Donor',
};

/** The typed text, settled: one request per pause, not one per keystroke. */
function useSettled(value: string, ms = 200): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = useSettled(text.trim());
  const search = useQuery({
    queryKey: ['global-search', q],
    queryFn: () => api.get<{ q: string; results: SearchResult[] }>(`/api/admin/search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
    staleTime: 15_000,
  });
  const results = q.length >= 2 ? (search.data?.results ?? []) : [];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        input.current?.focus();
        input.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => setActive(0), [q]);

  const go = (result: SearchResult | undefined) => {
    if (!result) return;
    setOpen(false);
    setText('');
    input.current?.blur();
    navigate(result.href);
  };
  const mac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

  return (
    <div className="relative px-3 pt-3">
      <label htmlFor="global-search" className="sr-only">
        Search everything
      </label>
      <input
        id="global-search"
        ref={input}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(results.length - 1, i + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            go(results[active]);
          } else if (e.key === 'Escape') {
            setOpen(false);
            input.current?.blur();
          }
        }}
        placeholder={`Search  ${mac ? '⌘K' : 'Ctrl+K'}`}
        autoComplete="off"
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls="global-search-results"
        className="w-full rounded border border-shell-700 bg-shell-900 px-2 py-1.5 text-sm text-white placeholder:text-shell-500 focus:border-white/40 focus:outline-none"
      />
      {open && q.length >= 2 ? (
        <div id="global-search-results" role="listbox" className="absolute top-full left-3 z-50 mt-1 max-h-[70vh] w-[26rem] overflow-auto rounded-lg border border-line bg-surface text-ink-900 shadow-lg">
          {search.isLoading ? <p className="px-3 py-2 text-sm text-ink-500">Searching…</p> : null}
          {!search.isLoading && results.length === 0 ? <p className="px-3 py-2 text-sm text-ink-500">Nothing matches “{q}”.</p> : null}
          {results.map((result, index) => (
            <button
              key={`${result.kind}:${result.id}`}
              type="button"
              role="option"
              aria-selected={index === active}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => go(result)}
              className={`block w-full border-b border-line px-3 py-2 text-left last:border-0 ${index === active ? 'bg-page' : ''}`}
            >
              <span className="mr-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">{KIND_LABEL[result.kind]}</span>
              <span className="text-sm font-medium">{result.title}</span>
              {result.detail ? <span className="block truncate text-xs text-ink-500">{result.detail}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
